import { useClinicalRecordLock } from '../../../../components/clinical-record-lock';
import React, { useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useParams } from 'react-router-dom';
import { enqueueSnackbar } from '../../../../components/snackbar';
import { DEService } from '../../../../api/clinical-pathways/des.service';
import { IHandoverSchema } from '../../../../@types/handover';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import { IClinicalRecordSchema } from '../../../../@types/clinicalRecord.type';
import SignatureDialog from '../../../../components/signature/SignatureDialog';
import { ClinicalRecords } from '../../../../api/clinicalRecords.service';
import SignedOffBanner from '../../../../components/signed-off-banner';
import {
  toUpperText,
  uppercaseElementValue,
} from '../../../../utils/uppercase';

interface FormValues {
  handoverDate: Date | null;
  handoverTo: string;
  handoverType: string;
  handoverReason: string;
  handoverNotes: string;

  screeningOutcome: string;
  nextScreeningDate: Date | null;
  screeningLocation: string;
  screeningNotes: string;

  referred: boolean;
  referralDate: Date | null;
  referralTo: string;
  referralUrgency: string;
  referralReason: string;
  referralNotes: string;

  treatmentRequired: boolean;
  treatmentType: string;
  treatmentStartDate: Date | null;
  treatmentProvider: string;
  treatmentNotes: string;

  followUpRequired: boolean;
  followUpDate: Date | null;
  followUpLocation: string;
  followUpInstructions: string;

  additionalNotes: string;
}

const HANDOVER_TYPES = [
  'Routine Handover',
  'Urgent Handover',
  'Referral to Specialist',
  'Transfer of Care',
  'Discharge',
  'Other',
];

const HANDOVER_REASONS = [
  'Routine Screening Completion',
  'Requires Specialist Care',
  'Complex Case Management',
  'Patient Request',
  'Clinical Indication',
  'Other',
];

const SCREENING_OUTCOMES = [
  'Normal - No DR',
  'Mild NPDR',
  'Moderate NPDR',
  'Severe NPDR',
  'Proliferative DR',
  'Diabetic Macular Edema',
  'Ungradable Images',
  'Other',
];

const REFERRAL_URGENCIES = ['Routine', 'Urgent', 'Emergency'];

const TREATMENT_TYPES = [
  'Laser Treatment',
  'Intravitreal Injections',
  'Surgical Intervention',
  'Medical Management',
  'Lifestyle Modification',
  'Other',
];

type Props = {
  clinicalRecordId?: number;
  desRecord?: any;
  onUpdate?: () => void;
  clinicalRecord?: IClinicalRecordSchema | any;
};

export const createHandoverOutcomeValidationSchema = (minAllowedDate: Date) =>
  Yup.object({
    handoverTo: Yup.string().required('Required'),
    handoverType: Yup.string().required('Required'),
    handoverReason: Yup.string().required('Required'),
    screeningOutcome: Yup.string().required('Required'),
    nextScreeningDate: Yup.date()
      .nullable()
      .when('screeningOutcome', {
        is: (val: string) => val && val !== 'Ungradable Images',
        then: (schema) =>
          schema
            .required('Next screening date is required')
            .min(
              minAllowedDate,
              'Next screening date cannot be before examination date',
            ),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
    referralTo: Yup.string().when('referred', {
      is: true,
      then: (schema) => schema.required('Referral to is required'),
    }),
    referralUrgency: Yup.string().when('referred', {
      is: true,
      then: (schema) => schema.required('Referral urgency is required'),
    }),
    treatmentType: Yup.string().when('treatmentRequired', {
      is: true,
      then: (schema) => schema.required('Treatment type is required'),
    }),
    followUpDate: Yup.date()
      .nullable()
      .when('followUpRequired', {
        is: true,
        then: (schema) =>
          schema
            .required('Follow-up date is required')
            .min(
              minAllowedDate,
              'Follow-up date cannot be before examination date',
            ),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
  });

export default function HandoverOutcomeForm({
  clinicalRecordId,
  desRecord,
  onUpdate,
  clinicalRecord,
}: Props) {
  const lock = useClinicalRecordLock();
  const { patientId } = useParams();
  const navigate = useNavigate();
  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const examDate = clinicalRecord?.createdDate
    ? new Date(clinicalRecord.createdDate)
    : null;
  const examDateStart = examDate
    ? new Date(examDate.getFullYear(), examDate.getMonth(), examDate.getDate())
    : null;
  const minAllowedDate =
    examDateStart && examDateStart > todayStart ? examDateStart : todayStart;

  const exitToPatientScreen = () => {
    navigate(PATH_DASHBOARD.patients.list);
  };
  const [isSignatureDialogOpen, setIsSignatureDialogOpen] =
    React.useState(false);
  const [isSigning, setIsSigning] = React.useState(false);
  const [pendingValues, setPendingValues] = React.useState<FormValues | null>(
    null,
  );

  const hasClinicalData = !!(
    clinicalRecord?.diabeticEyeScreeningId ||
    clinicalRecord?.mecsCuesId ||
    clinicalRecord?.glaucomaReferralRefinementId
  );

  const performHandover = async (values: FormValues) => {
    try {
      const service = new DEService();

      const payload = {
        ...values,
        clinicalRecordId,
        patientId,
        retinopathyGrade: values.screeningOutcome,
        reviewTimeline: values.nextScreeningDate
          ? values.nextScreeningDate.toISOString()
          : undefined,
        clinicalImpression: values.screeningNotes,
      };

      // Amending a signed record: the outcome fields live on the screening row,
      // so they are staged as the Diabetic Eye Screening section.
      if (
        lock.stageIfAmending(
          'diabeticEyeScreening',
          payload as Record<string, unknown>,
        )
      ) {
        return;
      }

      if (desRecord?.id) {
        await service.handoverDES(patientId!, desRecord.id, payload);
        enqueueSnackbar('Handover completed successfully', {
          variant: 'success',
        });
      } else {
        const res = await service.createDES(patientId!, payload);
        await service.handoverDES(patientId!, res.data.id, payload);
        enqueueSnackbar('Handover saved successfully', { variant: 'success' });
      }
      onUpdate && onUpdate();
    } catch (error) {
      enqueueSnackbar('Failed to save Handover', { variant: 'error' });
    }
  };

  const handleSignOff = async (signatureData: string) => {
    if (!clinicalRecordId) return;
    setIsSigning(true);
    try {
      const service = new ClinicalRecords();
      await service.signOff(clinicalRecordId, signatureData);
      enqueueSnackbar('Clinical record signed off successfully', {
        variant: 'success',
      });
      setIsSignatureDialogOpen(false);

      // PROCEED WITH HANDOVER AFTER SIGN-OFF
      if (pendingValues) {
        await performHandover(pendingValues);
        setPendingValues(null);
      } else {
        onUpdate && onUpdate();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to sign off clinical record', {
        variant: 'error',
      });
    } finally {
      setIsSigning(false);
    }
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      handoverDate: new Date(),
      handoverTo: '',
      handoverType: '',
      handoverReason: '',
      handoverNotes: '',

      screeningOutcome: '',
      nextScreeningDate: null,
      screeningLocation: '',
      screeningNotes: '',

      referred: false,
      referralDate: null,
      referralTo: '',
      referralUrgency: '',
      referralReason: '',
      referralNotes: '',

      treatmentRequired: false,
      treatmentType: '',
      treatmentStartDate: null,
      treatmentProvider: '',
      treatmentNotes: '',

      followUpRequired: false,
      followUpDate: null,
      followUpLocation: '',
      followUpInstructions: '',

      additionalNotes: '',
    },
    validationSchema: createHandoverOutcomeValidationSchema(minAllowedDate),
    onSubmit: async (values) => {
      if (hasClinicalData && !clinicalRecord?.signedAt) {
        setPendingValues(values);
        setIsSignatureDialogOpen(true);
      } else {
        await performHandover(values);
      }
    },
  });

  const handleHandoverClick = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      const fieldLabels: any = {
        handoverTo: 'Handover To',
        handoverType: 'Handover Type',
        handoverReason: 'Handover Reason',
        screeningOutcome: 'Screening Outcome',
        nextScreeningDate: 'Next Screening Date',
        referralTo: 'Referral To',
        referralUrgency: 'Referral Urgency',
        treatmentType: 'Treatment Type',
        followUpDate: 'Follow-up Date',
      };

      const missingFields = Object.keys(errors)
        .map((field) => fieldLabels[field] || field)
        .join(', ');

      enqueueSnackbar(`Missing required fields: ${missingFields}`, {
        variant: 'error',
      });

      // Mark all fields as touched to show inline errors
      const touched: any = {};
      Object.keys(errors).forEach((field) => {
        touched[field] = true;
      });
      formik.setTouched(touched);
    } else {
      formik.submitForm();
    }
  };

  useEffect(() => {
    if (desRecord && desRecord.id) {
      formik.setValues({
        handoverDate: desRecord.handoverDate
          ? new Date(desRecord.handoverDate)
          : new Date(),
        handoverTo: desRecord.handoverTo || '',
        handoverType: desRecord.handoverType || '',
        handoverReason: desRecord.handoverReason || '',
        handoverNotes: desRecord.handoverNotes || '',

        screeningOutcome: desRecord.retinopathyGrade || '',
        nextScreeningDate: desRecord.reviewTimeline
          ? new Date(desRecord.reviewTimeline)
          : null,
        screeningLocation: '', // Not in entity?
        screeningNotes: desRecord.clinicalImpression || '',

        referred: !!desRecord.referralRequired,
        referralDate: desRecord.referralDate
          ? new Date(desRecord.referralDate)
          : null, // Not in entity? I added referralNotes etc.
        // Wait, did I add referralDate?
        // Let's check entity update.
        referralTo: toUpperText(desRecord.referralTo || ''),
        referralUrgency: desRecord.referralUrgency || '',
        referralReason: desRecord.referralReason || '',
        referralNotes: desRecord.referralNotes || '',

        treatmentRequired: !!desRecord.treatmentRequired,
        treatmentType: desRecord.treatmentType || '',
        treatmentStartDate: desRecord.treatmentStartDate
          ? new Date(desRecord.treatmentStartDate)
          : null,
        treatmentProvider: desRecord.treatmentProvider || '',
        treatmentNotes: desRecord.treatmentNotes || '',

        followUpRequired: !!desRecord.followUpRequired,
        followUpDate: desRecord.followUpDate
          ? new Date(desRecord.followUpDate)
          : null,
        followUpLocation: desRecord.followUpLocation || '',
        followUpInstructions: desRecord.followUpInstructions || '',

        additionalNotes: desRecord.otherFindings || '',
      } as any);
    }
  }, [desRecord]);

  const renderHandoverDetails = () => (
    <Box sx={{ mb: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
        Handover Details
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Handover Date"
              value={formik.values.handoverDate}
              onChange={(date) => formik.setFieldValue('handoverDate', date)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                  error:
                    formik.touched.handoverDate &&
                    Boolean(formik.errors.handoverDate),
                },
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            required
            label="Handover To"
            name="handoverTo"
            value={formik.values.handoverTo}
            onChange={formik.handleChange}
            error={
              formik.touched.handoverTo && Boolean(formik.errors.handoverTo)
            }
            helperText={
              formik.touched.handoverTo && (formik.errors.handoverTo as any)
            }
            margin="normal"
            placeholder="e.g., GP, Retina Specialist"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl
            fullWidth
            margin="normal"
            required
            error={
              formik.touched.handoverType && Boolean(formik.errors.handoverType)
            }
          >
            <InputLabel>Handover Type</InputLabel>
            <Select
              name="handoverType"
              value={formik.values.handoverType}
              onChange={formik.handleChange}
              label="Handover Type"
            >
              {HANDOVER_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.handoverType && formik.errors.handoverType && (
              <FormHelperText>
                {formik.errors.handoverType as any}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl
            fullWidth
            margin="normal"
            required
            error={
              formik.touched.handoverReason &&
              Boolean(formik.errors.handoverReason)
            }
          >
            <InputLabel>Handover Reason</InputLabel>
            <Select
              name="handoverReason"
              value={formik.values.handoverReason}
              onChange={formik.handleChange}
              label="Handover Reason"
            >
              {HANDOVER_REASONS.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {reason}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.handoverReason && formik.errors.handoverReason && (
              <FormHelperText>
                {formik.errors.handoverReason as any}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Handover Notes"
            name="handoverNotes"
            value={formik.values.handoverNotes}
            onChange={formik.handleChange}
            multiline
            rows={3}
            margin="normal"
          />
        </Grid>
      </Grid>
    </Box>
  );

  const nextScreeningDateRequired =
    Boolean(formik.values.screeningOutcome) &&
    formik.values.screeningOutcome !== 'Ungradable Images';

  const renderScreeningOutcome = () => (
    <Box sx={{ mb: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
        Screening Outcome
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <FormControl
            fullWidth
            margin="normal"
            required
            error={
              formik.touched.screeningOutcome &&
              Boolean(formik.errors.screeningOutcome)
            }
          >
            <InputLabel>Screening Outcome</InputLabel>
            <Select
              name="screeningOutcome"
              value={formik.values.screeningOutcome}
              onChange={formik.handleChange}
              label="Screening Outcome"
            >
              {SCREENING_OUTCOMES.map((outcome) => (
                <MenuItem key={outcome} value={outcome}>
                  {outcome}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {(formik.touched.screeningOutcome &&
                (formik.errors.screeningOutcome as any)) ||
                'Retinopathy grade or screening result for this visit.'}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Next Screening Date"
              value={formik.values.nextScreeningDate}
              onChange={(date) =>
                formik.setFieldValue('nextScreeningDate', date)
              }
              minDate={minAllowedDate}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                  required: nextScreeningDateRequired,
                  error:
                    formik.touched.nextScreeningDate &&
                    Boolean(formik.errors.nextScreeningDate),
                  helperText:
                    (formik.touched.nextScreeningDate &&
                      (formik.errors.nextScreeningDate as any)) ||
                    (nextScreeningDateRequired
                      ? 'When the patient should return. Cannot be before the examination date.'
                      : 'Not required when outcome is Ungradable Images.'),
                },
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Screening Location"
            name="screeningLocation"
            value={formik.values.screeningLocation}
            onChange={formik.handleChange}
            margin="normal"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Screening Notes"
            name="screeningNotes"
            value={formik.values.screeningNotes}
            onChange={formik.handleChange}
            multiline
            rows={3}
            margin="normal"
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderReferralInformation = () => (
    <Box sx={{ mb: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
        Referral Information
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            name="referred"
            checked={formik.values.referred}
            onChange={(e) => {
              formik.setFieldValue('referred', e.target.checked);
              if (!e.target.checked) {
                formik.setFieldValue('referralDate', null);
                formik.setFieldValue('referralTo', '');
                formik.setFieldValue('referralUrgency', '');
                formik.setFieldValue('referralReason', '');
                formik.setFieldValue('referralNotes', '');
              }
            }}
          />
        }
        label="Patient Referred"
        sx={{ display: 'block', mb: 2 }}
      />
      {formik.values.referred && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Referral Date"
                value={formik.values.referralDate}
                onChange={(date) => formik.setFieldValue('referralDate', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: 'normal',
                    error:
                      formik.touched.referralDate &&
                      Boolean(formik.errors.referralDate),
                    helperText:
                      formik.touched.referralDate &&
                      (formik.errors.referralDate as any),
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Referred To"
              name="referralTo"
              value={formik.values.referralTo}
              onChange={(e) => {
                // Referral practitioner names are held in CAPITALS.
                uppercaseElementValue(e.target);
                formik.handleChange(e);
              }}
              error={
                formik.touched.referralTo && Boolean(formik.errors.referralTo)
              }
              helperText={
                formik.touched.referralTo && (formik.errors.referralTo as any)
              }
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl
              fullWidth
              margin="normal"
              required
              error={
                formik.touched.referralUrgency &&
                Boolean(formik.errors.referralUrgency)
              }
            >
              <InputLabel>Referral Urgency</InputLabel>
              <Select
                name="referralUrgency"
                value={formik.values.referralUrgency}
                onChange={formik.handleChange}
                label="Referral Urgency"
              >
                {REFERRAL_URGENCIES.map((urgency) => (
                  <MenuItem key={urgency} value={urgency}>
                    {urgency}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.referralUrgency &&
                formik.errors.referralUrgency && (
                  <FormHelperText>
                    {formik.errors.referralUrgency as any}
                  </FormHelperText>
                )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Referral Reason"
              name="referralReason"
              value={formik.values.referralReason}
              onChange={formik.handleChange}
              multiline
              rows={2}
              margin="normal"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Referral Notes"
              name="referralNotes"
              value={formik.values.referralNotes}
              onChange={formik.handleChange}
              multiline
              rows={3}
              margin="normal"
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );

  const renderTreatmentInformation = () => (
    <Box sx={{ mb: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
        Treatment Information
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            name="treatmentRequired"
            checked={formik.values.treatmentRequired}
            onChange={(e) => {
              formik.setFieldValue('treatmentRequired', e.target.checked);
              if (!e.target.checked) {
                formik.setFieldValue('treatmentType', '');
                formik.setFieldValue('treatmentStartDate', null);
                formik.setFieldValue('treatmentProvider', '');
                formik.setFieldValue('treatmentNotes', '');
              }
            }}
          />
        }
        label="Treatment Required"
        sx={{ display: 'block', mb: 2 }}
      />
      {formik.values.treatmentRequired && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <FormControl
              fullWidth
              margin="normal"
              required
              error={
                formik.touched.treatmentType &&
                Boolean(formik.errors.treatmentType)
              }
            >
              <InputLabel>Treatment Type</InputLabel>
              <Select
                name="treatmentType"
                value={formik.values.treatmentType}
                onChange={formik.handleChange}
                label="Treatment Type"
              >
                {TREATMENT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {(formik.touched.treatmentType &&
                  (formik.errors.treatmentType as any)) ||
                  'Primary treatment planned or in progress.'}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Treatment Start Date"
                value={formik.values.treatmentStartDate}
                onChange={(date) =>
                  formik.setFieldValue('treatmentStartDate', date)
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: 'normal',
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Treatment Provider"
              name="treatmentProvider"
              value={formik.values.treatmentProvider}
              onChange={formik.handleChange}
              margin="normal"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Treatment Notes"
              name="treatmentNotes"
              value={formik.values.treatmentNotes}
              onChange={formik.handleChange}
              multiline
              rows={3}
              margin="normal"
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );

  const renderFollowUpPlan = () => (
    <Box sx={{ mb: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
        Follow-up Plan
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            name="followUpRequired"
            checked={formik.values.followUpRequired}
            onChange={(e) => {
              formik.setFieldValue('followUpRequired', e.target.checked);
              if (!e.target.checked) {
                formik.setFieldValue('followUpDate', null);
                formik.setFieldValue('followUpLocation', '');
                formik.setFieldValue('followUpInstructions', '');
              }
            }}
          />
        }
        label="Follow-up Required"
        sx={{ display: 'block', mb: 2 }}
      />
      {formik.values.followUpRequired && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Follow-up Date"
                value={formik.values.followUpDate}
                onChange={(date) => formik.setFieldValue('followUpDate', date)}
                minDate={minAllowedDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: 'normal',
                    required: true,
                    error:
                      formik.touched.followUpDate &&
                      Boolean(formik.errors.followUpDate),
                    helperText:
                      (formik.touched.followUpDate &&
                        (formik.errors.followUpDate as any)) ||
                      'When the patient should be seen again. Cannot be before the examination date.',
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Follow-up Location"
              name="followUpLocation"
              value={formik.values.followUpLocation}
              onChange={formik.handleChange}
              margin="normal"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Follow-up Instructions"
              name="followUpInstructions"
              value={formik.values.followUpInstructions}
              onChange={formik.handleChange}
              multiline
              rows={3}
              margin="normal"
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {!hasClinicalData && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="warning.main" gutterBottom>
            No clinical data found.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please complete at least one clinical form before signing off.
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            flexWrap="wrap"
          >
            <LoadingButton variant="contained" disabled>
              Sign Off Clinical Record
            </LoadingButton>
            <LoadingButton variant="outlined" disabled>
              Do Handover
            </LoadingButton>
            <LoadingButton variant="outlined" onClick={exitToPatientScreen}>
              Exit
            </LoadingButton>
          </Stack>
        </Box>
      )}

      {hasClinicalData && !clinicalRecord?.signedAt && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="info.main" gutterBottom>
            Clinical record must be signed off before handover.
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            flexWrap="wrap"
          >
            <LoadingButton
              variant="contained"
              color="primary"
              onClick={() => setIsSignatureDialogOpen(true)}
            >
              Sign Off Clinical Record
            </LoadingButton>
            <LoadingButton variant="outlined" onClick={exitToPatientScreen}>
              Exit
            </LoadingButton>
          </Stack>
        </Box>
      )}

      {hasClinicalData && clinicalRecord?.signedAt && (
        <SignedOffBanner
          signedBy={clinicalRecord.signedByUser?.username}
          signedAt={clinicalRecord.signedAt}
          sx={{ mt: 3, mb: 2, mx: 3 }}
        />
      )}

      {hasClinicalData && clinicalRecord?.signedAt && (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Handover & Outcome
          </Typography>
          {renderHandoverDetails()}
          {renderScreeningOutcome()}
          {renderReferralInformation()}
          {renderTreatmentInformation()}
          {renderFollowUpPlan()}
          <Divider sx={{ my: 3 }} />
          <TextField
            fullWidth
            label="Additional Notes"
            name="additionalNotes"
            value={formik.values.additionalNotes}
            onChange={formik.handleChange}
            multiline
            rows={4}
            margin="normal"
          />
          <Divider sx={{ my: 3 }} />
          <Stack
            direction="row-reverse"
            mt="40px"
            gap="30px"
            mr="17px"
            mb="20px"
          >
            <LoadingButton
              type="button"
              variant="contained"
              onClick={exitToPatientScreen}
            >
              Exit
            </LoadingButton>
            <LoadingButton
              type="button"
              variant="outlined"
              loading={formik.isSubmitting}
              onClick={handleHandoverClick}
            >
              Do Handover
            </LoadingButton>
          </Stack>
        </Box>
      )}
      <SignatureDialog
        open={isSignatureDialogOpen}
        onClose={() => setIsSignatureDialogOpen(false)}
        onConfirm={handleSignOff}
        loading={isSigning}
      />
    </LocalizationProvider>
  );
}
